import {
    Component,
    EventEmitter,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { Observable, of, Subject, combineLatest } from 'rxjs';
import { switchMap, take, takeUntil, tap, finalize } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
    Finance,
    FinanceExtractQuery,
    FinanceSearchQuery,
    FinanceStatus,
    FinanceType,
} from '../finance.types';
import { FinanceService } from '../finance.service';
import { LoginService } from '@intranet/modules/login';
import { MatDialog } from '@angular/material/dialog';
import { HotkeyService } from 'src/app/core/services/hotkey.service';
import { SnackbarService } from '@intranet/core/services/snackbar.service';
import { FinanceDeleteDialog } from '../dialogs/delete/finance-delete-dialog.component';
import {
    convertFinanceTypeToName,
    getStatusesByFinanceType,
    navigateToConvert,
} from '../finance.helpers';
import { FinanceApproveDialog } from '../dialogs/approve/finance-approve-dialog.component';
import { FinanceAlertDialog } from '../dialogs/alert/finance-alert-dialog.component';
import { FinanceExtractDialog } from '../dialogs/extract/finance-extract-dialog.component';
import { PageEvent } from '@angular/material/paginator';
import moment from 'moment';
import { ActionService } from '@intranet/modules/actions/action.service';
import { ActionManageDialog } from '@intranet/modules/actions/dialogs/action-manage/action-manage-dialog.component';
import { JobAction, JobActionUpdate } from '@intranet/modules/actions/action.types';

@Component({
    selector: 'finance-listing',
    templateUrl: './finance-listing.component.html',
    styleUrls: ['./finance-listing.component.scss'],
})
export class FinanceListingComponent implements OnInit, OnDestroy {
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _search$ = new Subject<FinanceSearchQuery>();
    isLoading: boolean = false;
    filtersReady: boolean = false;
    statuses: FinanceStatus[];
    financeType: FinanceType;
    filterQuery: FinanceSearchQuery;
    createUrl: string;
    canCreate: boolean;
    canDelete: boolean;
    totalResults = 0;
    // Passed to ActionManageDialog as its required 'updated' EventEmitter -
    // the dialog calls .emit() on it internally; we don't need to listen,
    // since afterClosed() already triggers a full refresh below.
    private _actionDialogChanged = new EventEmitter<{ action: JobAction; type: string }>();
    isMonthEnd: boolean = false;
    private _skipNextUrlSync = true;

    constructor(
        public dialog: MatDialog,
        protected loginService: LoginService,
        private _financeService: FinanceService,
        private _actionService: ActionService,
        private _router: Router,
        private _route: ActivatedRoute,
        private hotkeyService: HotkeyService,
        private _snackbar: SnackbarService,
    ) {}

    ngOnInit() {
        combineLatest([
            this._financeService.financeStatuses$,
            this._financeService.filterQuery$,
        ])
            .pipe(
                take(1),
                tap(([statuses, filterQuery]) => {
                    this.financeType = this._route.snapshot.data.financeType;
                    this.isMonthEnd = !!this._route.snapshot.data.monthEnd;
                    this.statuses = getStatusesByFinanceType(
                        statuses,
                        this.financeType
                    );
                    this.filterQuery = filterQuery;
                    this.createUrl = `/finance/${convertFinanceTypeToName(
                        this.financeType,
                        true
                    )}/create`;

                    // Anyone can create a quote, but only finance folks can create an invoice from scratch
                    this.canCreate =
                        this.financeType === 1 ||
                        (this.financeType === 2 &&
                            this.loginService.checkAuth(['finance']));

                    // Finance or Admin role can delete anything - there is also another check in the listing table
                    // where if the finance has not been through an approval, and the logged in user is the creator, then they can also delete
                    this.canDelete = this.loginService.checkAuth([
                        'finance',
                        'admin',
                    ]);
                })
            )
            .subscribe({
                next: () => (this.isLoading = false),
                complete: () => (this.isLoading = false),
            });

        // Register the hotkey for "Add" action
        this.hotkeyService.registerHotkey(
            'ctrl+shift+a',
            'Add new finance item',
            () => this.checkAndAdd()
        );

        this._financeService.filterQuery$
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap((filterQuery) => {
                    this.filterQuery = filterQuery;

                    // Keep the URL in sync with the month-end filters so
                    // that using the browser's back button (e.g. from the
                    // linked job page) restores them exactly as they were
                    // left. Skip the very first emission on mount - that's
                    // just the pre-restore value, and syncing it here would
                    // race with (and clobber) the search component's own
                    // read of the URL on init.
                    if (this.isMonthEnd) {
                        if (this._skipNextUrlSync) {
                            this._skipNextUrlSync = false;
                        } else {
                            this._syncFiltersToUrl(filterQuery);
                        }
                    }
                })
            )
            .subscribe();

        this._financeService.financesTotalResults$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (finances) => {
                    this.totalResults = finances;
                },
            });

        this._search$
        .pipe(
            takeUntil(this._unsubscribeAll),
            switchMap((query) => {
                this.isLoading = true;
                return this.loadFinances(query).pipe(
                    finalize(() => this.isLoading = false)
                );
            })
        )
        .subscribe();
    }

    ngOnDestroy() {
        this._unsubscribeAll.next('');
        this._unsubscribeAll.complete();
        this._financeService.reset();
    }

    checkAndAdd(): void {
        const currentUrl = this._router.url;

        // Ensure the hotkey only works for finance-related pages
        if (!currentUrl.startsWith('/finance/')) {
            return;
        }

        // Prevent the hotkey from doing anything on /finance/extracts
        if (currentUrl.includes('/finance/extracts')) {
            return;
        }

        // Dynamically set createUrl based on the current page
        if (currentUrl.includes('/finance/estimates')) {
            this.createUrl = '/finance/estimates/create';
        } else if (currentUrl.includes('/finance/invoices')) {
            this.createUrl = '/finance/invoices/create';
        }

        // Navigate to the dynamically set URL if createUrl is valid
        if (this.createUrl) {
            this._router.navigateByUrl(this.createUrl);
        }
    }

    // Call the API via the finance service
    loadFinances(query?: FinanceSearchQuery): Observable<Finance[]> {
        return this._financeService.getFinances(query);
    }

    // Mirrors the month-end filters into the URL's query params, so the
    // browser's back button restores them (see the read side in
    // finance-listing-search.component.ts). Any field left out here (e.g.
    // sortBy/status tab/page) simply isn't part of the "leave as I left
    // it" contract for now.
    private _syncFiltersToUrl(query: FinanceSearchQuery): void {
        this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {
                clientId: query.clientId || null,
                startDate: query.startDate
                    ? moment(query.startDate).format('YYYY-MM-DD')
                    : null,
                endDate: query.endDate
                    ? moment(query.endDate).format('YYYY-MM-DD')
                    : null,
                userId: query.userId || null,
                searchTerm: query.searchTerm || null,
            },
            queryParamsHandling: 'merge',
            // Replace rather than push: keeps /finance/monthend as a single,
            // continuously-updated history entry instead of one per filter
            // tweak, so there's nothing for the browser's back button to
            // step through inconsistently while the user stays on this page
            replaceUrl: true,
        });
    }

    // Called by the main filters (client, date, created by, search)
    onFilter(filterQuery: FinanceSearchQuery) {
        this._search$.next({
            ...filterQuery,
            financeType: this.financeType,
            page: 1,
            isPaginating: false,
        });
    }

    onExtract(extractQuery: FinanceExtractQuery) {
        const dialogRef = this.dialog.open(FinanceExtractDialog, {
            width: '320px',
            data: {
                title: extractQuery.title,
                includeCompletedOnly: true,
                excludeExistingExtract: true
            },
        });

        dialogRef
            .afterClosed()
            .pipe(
                switchMap((result) => {
                    if (result) {
                        this.isLoading = true;
                        return this._financeService
                            .createExtract({
                                ...extractQuery,
                                ...result,
                            })
                            .pipe(finalize(() => (this.isLoading = false)));
                    }

                    return of(false);
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe({
                next: (created) => {
                    if (created) {
                        this._snackbar.success(
                            'Extract created — open the Extracts page to view it.'
                        );
                    }
                },
                error: () => {
                    this._snackbar.error(
                        'Sorry, the extract could not be created. Please try again.'
                    );
                },
            });
    }

    // Called by switching the status tabs
    onStatus(financeStatusCode: string | 'All'): void {
        if (financeStatusCode === 'All') financeStatusCode = null;
        this._search$.next({
            ...this.filterQuery,
            financeStatusCode,
            page: 1,
            isPaginating: false,
        });
    }

    // Loads more rows for the current query
    onPaginate(e: PageEvent) {
        this._search$.next({
            ...this.filterQuery,
            page: e.pageIndex + 1,
            isPaginating: true,
        });

        // optional: scroll top
        setTimeout(() => {
            document.getElementById('page')?.scrollTo(0, 0);
        });
    }

    onLoading(loading: boolean) {
        this.isLoading = loading;
    }

    onCopy(finance: Finance) {
        this._router.navigateByUrl(
            `/finance/${convertFinanceTypeToName(finance.financeType, true)}/${
                finance.version.id
            }/copy`
        );
    }

    onSort(prop: string) {
        const { sortBy: prevSortBy, sortDir: prevSortDir } = this.filterQuery;
        this._search$.next({
            ...this.filterQuery,
            sortBy: prop,
            sortDir:
                prop === prevSortBy
                    ? prevSortDir === 'asc'
                        ? 'desc'
                        : 'asc'
                    : 'asc',
        });
    }

    async onNextStatusStep(finance: Finance) {
        let financeId = finance.financeId;
        let financeType = finance.financeType;
        let versionId = finance.version.id;
        let nextStatus = finance.nextStatus;

        if (nextStatus) {
            if (nextStatus.requiresApproval) {
                if (
                    !this.loginService.checkAuth([
                        'finance',
                        'manager',
                        'admin',
                    ])
                ) {
                    this.showNoPermissonModal();
                } else {
                    this.showApprovalModal(finance, nextStatus);
                }
            } else {
                this._financeService
                    .createVersionStatus(
                        financeId,
                        versionId,
                        nextStatus.financeStatusCode
                    )
                    .subscribe({
                        next: () => {
                            this._search$.next(this.filterQuery);
                        }
                    });
            }
        } else {
            navigateToConvert(versionId, financeType, this._router);
        }
    }

    showNoPermissonModal() {
        this.dialog.open(FinanceAlertDialog, {
            width: '320px',
            data: {
                title: 'Manager approval required',
            },
        });
    }

    showApprovalModal(finance: Finance, nextStatus: FinanceStatus) {
        const dialogRef = this.dialog.open(FinanceApproveDialog, {
            width: '320px',
            data: { finance, nextStatus },
        });

        dialogRef
            .afterClosed()
            .pipe(
                switchMap((result) => {
                    if (result) {
                        return this._financeService.createVersionStatus(
                            finance.financeId,
                            finance.version.id,
                            nextStatus.financeStatusCode,
                            result.action === 'reject'
                        );
                    }

                    return of(false);
                }),
                tap(() => this._search$.next(this.filterQuery))
            )
            .subscribe();
    }

    onDelete(finance: Finance): void {
        const dialogRef = this.dialog.open(FinanceDeleteDialog, {
            width: '350px',
            data: { finance },
        });

        dialogRef
            .afterClosed()
            .pipe(
                tap((result) => {
                    if (result) {
                        this._financeService
                            .delete(result.financeId, result.fullDelete)
                            .subscribe({
                                next: () => this._search$.next(this.filterQuery)
                            });
                    }
                })
            )
            .subscribe();
    }

    onCreate() {
        this._router.navigateByUrl(`/finance/${this.financeType}/create`);
    }

    // Inline Scheduled/Deadline date edits from the Month End table. Mirrors
    // the validation already applied in action-listing.component.ts's
    // onActionUpdate, then writes back via the same ActionService.update()
    // (PATCH api/action) call used everywhere else actions are edited.
    onActionUpdate(action: JobAction): void {
        if (action.title != null && action.title.trim() === '') {
            this._snackbar.error('Action name cannot be empty');
            return;
        }

        if (action.deadlineDate && !action.scheduleDate) {
            this._snackbar.error(
                'You must set a scheduled date before setting a deadline date'
            );
            return;
        }

        if (
            action.deadlineDate &&
            action.scheduleDate &&
            moment(action.deadlineDate).isBefore(action.scheduleDate)
        ) {
            this._snackbar.error(
                'Deadline date cannot be earlier than the scheduled date'
            );
            return;
        }

        this._actionService.update(action).subscribe({
            next: () => this._search$.next(this.filterQuery),
            error: () =>
                this._snackbar.error(
                    'Sorry, the action could not be updated. Please try again.'
                ),
        });
    }

    // Opens the same ActionManageDialog used on jobs/detail and the Actions
    // module for Action Status edits from the Month End table's Status cell.
    onActionSelected({ action, propertyKey }: JobActionUpdate): void {
        const dialogRef = this.dialog.open(ActionManageDialog, {
            width: '980px',
            data: {
                jobActionId: action.jobActionId,
                jobId: action.jobId,
                propertyKey,
                title: propertyKey === 'actionStatus' ? undefined : action.title,
                nextActionStatus: null,
                updated: this._actionDialogChanged,
            },
        });

        dialogRef.afterClosed().subscribe({
            complete: () => this._search$.next(this.filterQuery),
        });
    }
}
